import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, forkJoin, map, Observable, of, tap, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { GameSectionService } from '../../game-section/data/game-section.service';
import type {
  BlogListResponse,
  BlogShowResponse,
  BlogTaxonomyResponse,
} from './blog.api-model';
import {
  BLOG_GENERAL_GAME,
  gameSummaryToBlogGameRef,
  toBlogArticleView,
  toBlogChips,
  toBlogListPage,
} from './blog.mappers';
import type {
  BlogArticleView,
  BlogListFilters,
  BlogListPage,
  BlogTaxonomies,
} from './blog-view.model';

/**
 * Lo que viaja del SSR al navegador por `TransferState` (B121): el resultado,
 * o la marca de que el servidor falló, para que el cliente pinte lo mismo.
 */
type Transferred<T> = { ok: true; value: T } | { ok: false };

/** Error que relanza el navegador cuando el SSR falló (B121). */
export class BlogTransferredError extends Error {
  constructor() {
    super('La petición del blog falló en el servidor (TransferState).');
    this.name = 'BlogTransferredError';
  }
}

/** Filtros del listado en orden fijo, sin los vacíos (B120, B121). */
const LIST_FILTER_KEYS = ['game', 'category', 'tag', 'search'] as const;

function listParams(filters: BlogListFilters, page: number): [string, string][] {
  const params: [string, string][] = [];
  for (const key of LIST_FILTER_KEYS) {
    const value = filters[key];
    if (value) params.push([key, value]);
  }
  params.push(['page', String(page)]);
  return params;
}

/**
 * Clave del listado: la misma query da la misma clave en servidor y cliente,
 * sea cual sea el orden de los query params de la URL.
 */
export function blogListStateKey(
  filters: BlogListFilters,
  page: number,
): StateKey<Transferred<BlogListPage>> {
  const query = new URLSearchParams(listParams(filters, page)).toString();
  return makeStateKey('blog-list:' + query);
}

export function blogPostStateKey(slug: string): StateKey<Transferred<BlogArticleView | null>> {
  return makeStateKey('blog-post:' + slug);
}

/** Categorías y etiquetas; los juegos viajan con la clave de `GameSectionService`. */
export const BLOG_TAXONOMIES_STATE_KEY = makeStateKey<
  Transferred<Pick<BlogTaxonomies, 'categories' | 'tags'>>
>('blog-taxonomy-chips');

/**
 * Única costura entre el blog y sus datos (B59), contra la API pública del
 * blog (B120). Las páginas y los resolvers SOLO hablan con este servicio.
 *
 * Contrato:
 * - `getBySlug` devuelve `null` en un 404 (entrada que no existe o no es
 *   visible); cualquier otro error se propaga. El `post.slug` de la vista es
 *   el CANÓNICO: si difiere del pedido, el slug está retirado (301, B122).
 * - `list` ecoa en `applied` solo los filtros resueltos (B72) y trae el
 *   destacado aparte solo sin filtros y en la página 1 (B10, E8).
 * - `taxonomies` no falla nunca: es la decoración de la barra de filtros.
 *
 * SSR sin doble fetch (B121): el transfer cache de Angular no cubre peticiones
 * con `Authorization` (P39), así que cada resultado viaja por `TransferState`
 * con la semántica de B106 (el servidor guarda; el navegador lo lee una sola
 * vez y lo borra). A diferencia de W4, también viaja el FALLO: el navegador lo
 * relanza sin pedir y el resolver pinta el mismo estado que el servidor.
 * `withTransferState` (shared) no sirve: descarta los `null` del 404.
 */
@Injectable({ providedIn: 'root' })
export class BlogService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly gameSections = inject(GameSectionService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Página `page` (base 1) del listado con los filtros dados. */
  list(filters: BlogListFilters, page: number): Observable<BlogListPage> {
    // Sin `per_page`: el default de la API (9) es la rejilla del diseño (B120).
    const params = new HttpParams({ fromObject: Object.fromEntries(listParams(filters, page)) });
    return this.transferOnce(
      blogListStateKey(filters, page),
      this.http.get<BlogListResponse>(this.url('/posts'), { params }).pipe(map(toBlogListPage)),
    );
  }

  /** Ficha de un artículo con su navegación, o `null` si no existe (404). */
  getBySlug(slug: string): Observable<BlogArticleView | null> {
    return this.transferOnce(
      blogPostStateKey(slug),
      this.http.get<BlogShowResponse>(this.url(`/posts/${encodeURIComponent(slug)}`)).pipe(
        map(toBlogArticleView),
        catchError((err: unknown) =>
          err instanceof HttpErrorResponse && err.status === 404 ? of(null) : throwError(() => err),
        ),
      ),
    );
  }

  /**
   * Juegos, categorías y etiquetas de la barra de filtros. Los juegos son las
   * secciones publicadas de `GET /web/games` más «General» al final, que la
   * API resuelve en `?game=general` (B84, P42). Cada fuente que falla se queda
   * en `[]`.
   */
  taxonomies(): Observable<BlogTaxonomies> {
    const chips$ = this.transferOnce(
      BLOG_TAXONOMIES_STATE_KEY,
      forkJoin({
        categories: this.chips('/categories'),
        tags: this.chips('/tags'),
      }),
    );
    const games$ = this.gameSections.getGames().pipe(
      map((rows) => [...rows.map(gameSummaryToBlogGameRef), BLOG_GENERAL_GAME]),
      catchError(() => of([BLOG_GENERAL_GAME])),
    );
    return forkJoin({ chips: chips$, games: games$ }).pipe(
      map(({ chips, games }) => ({ games, ...chips })),
      catchError(() => of({ games: [BLOG_GENERAL_GAME], categories: [], tags: [] })),
    );
  }

  private chips(path: string) {
    return this.http.get<BlogTaxonomyResponse>(this.url(path)).pipe(
      map((res) => toBlogChips(res?.data)),
      catchError(() => of([])),
    );
  }

  /**
   * B106 + fallo transferido (B121). En el navegador, si el servidor dejó la
   * clave, se consume una sola vez (volver al mismo listado más tarde pide
   * fresco) y no hay petición.
   */
  private transferOnce<T>(key: StateKey<Transferred<T>>, source$: Observable<T>): Observable<T> {
    if (this.isBrowser && this.transferState.hasKey(key)) {
      const snapshot = this.transferState.get(key, null);
      this.transferState.remove(key);
      if (snapshot?.ok === true) return of(snapshot.value);
      if (snapshot?.ok === false) return throwError(() => new BlogTransferredError());
    }

    if (this.isBrowser) return source$;

    return source$.pipe(
      tap((value) => this.transferState.set(key, { ok: true, value })),
      catchError((err: unknown) => {
        this.transferState.set(key, { ok: false });
        return throwError(() => err);
      }),
    );
  }

  private url(path: string): string {
    return `${environment.apiUrl}/web/blog${path}`;
  }
}
